import { Box, Typography } from "@mui/material";
import { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import BaseReactTooltip from "../BaseReactTooltip";
import CloseIcon from '@mui/icons-material/Close';
import Icon1 from "../../assets/images/reports-icons/gene-report-1.png";
import Icon2 from "../../assets/images/reports-icons/gene-report-2.png";
import Icon3 from "../../assets/images/reports-icons/gene-report-3.png";
import Icon4 from "../../assets/images/reports-icons/gene-report-4.png";
import Icon5 from "../../assets/images/reports-icons/gene-report-5.png";

import { GetGeneLinks } from "../../apollo-client/api-query";
import { useLazyQuery } from '@apollo/client';
import { openModal } from "../../store";

const bodyStyle = {
    fontFamily: 'Poppins',
    fontSize: '12px',
    fontWeight: 300,
    lineHeight: '12px'
};

const iconRowStyle = {
    borderTop: '1px solid #ffffff', 
    display: 'flex', 
    justifyContent: 'flex-start', 
    height: '23px',
    cursor: 'pointer'
};

const iconRowText = {
    fontFamily: 'Poppins',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '23px',
    color: '#ffffff'
};

const GeneButton = ({ styleProps, text, isDot = true }) => {
    const dispatch = useDispatch();

    const [isOpen, setIsOpen] = useState(false);
    const [geneData, setGeneData] = useState([]);
    const tooltipRef = useRef(null);

    const [getGeneLinks, { loading: loadingFetch }] = useLazyQuery(GetGeneLinks, {
        fetchPolicy: 'no-cache',
    });

    // Fetch gene data
    const fetchData = useCallback(async () => {
        if (!loadingFetch) {
            const result = await getGeneLinks({
                variables: {
                    geneNames: [text],
                }
            });

            if (result?.data?.geneLinks?.edges?.length) {
                const tempArr = result.data.geneLinks.edges.map(edge => ({
                    gene: edge.node.gene,
                    pharmGkbUrl: edge.node.pharmGkbUrl,
                    monarchInitiativePharmgkbUrl: edge.node.monarchInitiativePharmgkbUrl,
                    geneCardUrl: edge.node.geneCardUrl,
                    medlinePlusUrl: edge.node.medlinePlusUrl,
                    omimUrl: edge.node.omimUrl,
                    summary: edge.node.summary,
                }));
                setGeneData(tempArr);
            }
        }
    }, [getGeneLinks, loadingFetch]);

    // Handle outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);
      
    const openNewTab = (url) => {
        window.open(url, "_blank");
    };

    const handleClickLinkBtn = (url) => {
        dispatch(
            openModal({
                currentModal: 'OPEN_EXTERNAL_MODAL',
                modalProps: {
                    onConfirm: () => openNewTab(url)
                }
            })
        );
    };

    const handleOpenTooltip = async () => {
        await fetchData();
        setIsOpen(true)
    }

    return (
        <div ref={tooltipRef}>
            <Typography 
                color='primary.mainBlue' 
                variant="subText"
                id={text}
                style={{
                    width: 'fit-content',
                    cursor: 'pointer',
                    fontWeight: '500',
                    lineHeight: '18px',
                    ...styleProps
                }}
                onClick={handleOpenTooltip}
            >
                {text}
                {isDot ? ', ' : ''}&nbsp;
            </Typography>
            
            <BaseReactTooltip 
                anchorSelect={`#${text}`}
                place="bottom-end"
                opacity={1}
                openOnClick={true}
                clickable={true}
                isOpen={isOpen}
                style={{
                    background: '#3C474F',
                    borderRadius: '8px',
                    color: '#1B1C1E',
                    boxShadow: '0px 2px 10px 0px #5B87EF33',
                    zIndex: 20,
                    padding: 0,
                    width: '380px',
                    cursor: 'default'
                }}
            >
                <CloseIcon
                    sx={{
                        width: '25px',
                        height: '25px',
                        color: '#ffffff',
                        cursor: 'pointer',
                        position: 'absolute',
                        top: '10px',
                        right: '10px'
                    }}
                    onClick={() => setIsOpen(false)}
                />
                <Box
                    sx={{
                        color: 'primary.darkBlack',
                        padding: '13px 24px',
                        width: '100%',
                        maxWidth: '380px',
                        cursor: 'default'
                    }}
                >
                    <Typography variant="h4" style={{ fontWeight: '400', width: '100%' }} color='primary.white'>
                        {text}
                    </Typography>
                    <Typography style={{ ...bodyStyle, marginTop: '4px', lineHeight: 1.5 }} color='primary.white'>
                        {geneData[0]?.summary}
                    </Typography>
                </Box>
                <Box style={{ padding: '0 10px', paddingBottom: '4px' }}>
                    {[{ icon: Icon1, url: 'pharmGkbUrl', text: 'PharmGKB' },
                      { icon: Icon2, url: 'medlinePlusUrl', text: 'MedLinePlus' },
                      { icon: Icon3, url: 'monarchInitiativePharmgkbUrl', text: 'Monarch Initiative' },
                      { icon: Icon4, url: 'omimUrl', text: 'OMIM' },
                      { icon: Icon5, url: 'geneCardUrl', text: 'GeneCards' }]
                        .map(({ icon, url, text }) => {
                            const geneLink = geneData[0]?.[url];
                            if (!geneLink) return null;
                            return <Box 
                                        key={text}
                                        style={iconRowStyle}
                                        onClick={() => handleClickLinkBtn(geneLink)}
                                    >
                                        <div style={{
                                            width: '50px', 
                                            display: 'flex', 
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}>
                                            <img 
                                                src={icon}
                                                style={{
                                                    width: '18px',
                                                    height: '12px'
                                                }}
                                            />
                                        </div>
                                        <Typography style={iconRowText}>
                                            Read more on {text}
                                        </Typography>
                                    </Box>
                        })}
                </Box>
            </BaseReactTooltip>
        </div>
    );
};

export default GeneButton;
